import { HoverCard } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";

import {
  InlineSourceCitation,
  type InlineSourceCitationDensity,
} from "./InlineSourceCitation";
import { SourceCountCluster } from "./SourceCountCluster";
import { SourceHoverPanel } from "./SourceHoverPanel";
import { type AskPunttSource } from "./types";

export const SOURCE_HOVER_OPEN_DELAY_MS = 200;
export const SOURCE_HOVER_CLOSE_DELAY_MS = 150;

export type SourceHoverCardTrigger = "chip" | "count";

export type SourceHoverCardProps = {
  sources: AskPunttSource[];
  testIdPrefix: string;
  /** Stable id for the hover content testid (group of sources at one citation). */
  groupId: string;
  trigger?: SourceHoverCardTrigger;
  /** Chip density when trigger is "chip". */
  density?: InlineSourceCitationDensity;
  /** Visible chip text in place of the first source's title. */
  chipLabel?: string;
  /** The chip's accessible name when `chipLabel` is not the title; it must contain that label. */
  chipAriaLabel?: string;
  /** Keeps a chip's existing testid when this card wraps a badge another surface already names. */
  chipTestId?: string;
  /** Accent fill for the chip the reader has focused. */
  selected?: boolean;
  chipClassName?: string;
  /** Click on the chip or count, e.g. to open the full source beyond the card. */
  onTriggerClick?: () => void;
  /** Controlled open — tests can force the card visible. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
};

export function SourceHoverCard({
  sources,
  testIdPrefix,
  groupId,
  trigger = "chip",
  density = "block",
  chipLabel,
  chipAriaLabel,
  chipTestId,
  selected,
  chipClassName,
  onTriggerClick,
  open: openProp,
  onOpenChange,
  side = "top",
  align = "start",
}: SourceHoverCardProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : uncontrolledOpen;

  // why: a click that lands inside the open delay leaves Radix's pending open
  // timer running, so the card reappears over the dialog the click opened.
  // Opens are ignored from the click until the pointer leaves the trigger.
  const clickedRef = useRef(false);
  const setOpen = (next: boolean) => {
    if (next && clickedRef.current) return;
    if (!controlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  // why: remount the panel when the card closes so pager index resets without
  // lifting index state into this wrapper.
  const [panelKey, setPanelKey] = useState(0);
  useEffect(() => {
    if (!open) setPanelKey((prev) => prev + 1);
  }, [open]);

  if (sources.length === 0) return null;

  const extraCount = Math.max(0, sources.length - 1);
  // why close first: whatever the click opens (a dialog) sits under the
  // card's portal otherwise, with the preview floating over it.
  const handleTriggerClick = onTriggerClick
    ? () => {
        setOpen(false);
        clickedRef.current = true;
        onTriggerClick();
      }
    : undefined;
  const handleTriggerLeave = () => {
    clickedRef.current = false;
  };

  const triggerNode =
    trigger === "count" ? (
      <SourceCountCluster
        sources={sources}
        testIdPrefix={testIdPrefix}
        data-testid={`${testIdPrefix}-source-count`}
        onClick={handleTriggerClick}
        onPointerLeave={handleTriggerLeave}
      />
    ) : (
      <InlineSourceCitation
        source={sources[0]!}
        testIdPrefix={testIdPrefix}
        density={density}
        extraCount={extraCount}
        label={chipLabel}
        ariaLabel={chipAriaLabel}
        testId={chipTestId}
        selected={selected}
        className={chipClassName}
        data-testid={`${testIdPrefix}-inline-source-${sources[0]!.id}`}
        onClick={handleTriggerClick}
        onPointerLeave={handleTriggerLeave}
      />
    );

  return (
    <HoverCard.Root
      open={open}
      onOpenChange={setOpen}
      openDelay={SOURCE_HOVER_OPEN_DELAY_MS}
      closeDelay={SOURCE_HOVER_CLOSE_DELAY_MS}
    >
      <HoverCard.Trigger>{triggerNode}</HoverCard.Trigger>
      <HoverCard.Content
        side={side}
        align={align}
        sideOffset={8}
        collisionPadding={16}
        className="z-50 border-0 bg-transparent p-0 shadow-none"
      >
        <SourceHoverPanel
          key={panelKey}
          sources={sources}
          testIdPrefix={testIdPrefix}
          groupId={groupId}
        />
      </HoverCard.Content>
    </HoverCard.Root>
  );
}
